import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import { X, Phone, MessageCircle, Clock, CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * PhoneVerificationGate — shown to a player whose first-withdrawal was blocked.
 *
 * Three sub-states:
 *   - NOT_SUBMITTED: show WhatsApp CTA + phone input + "I've texted you" submit
 *   - PENDING: show "Waiting for admin review" with the phone they submitted
 *   - REJECTED: show rejection reason + "Try again" button returning to NOT_SUBMITTED
 *
 * Closing and reopening does not lose state — the server is the source of truth
 * via /api/users/me/phone-verification/status.
 */
export default function PhoneVerificationGate({ open, onClose, onVerified }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/users/me/phone-verification/status');
      setData(r.data);
      if (r.data?.verified && onVerified) onVerified();
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    reload();
    // Poll every 8s while the dialog is open so an admin approval flips
    // the UI without the player needing to refresh.
    const t = setInterval(reload, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const waNumber = (data?.whatsapp_number || '').trim();
  const waDigits = waNumber.replace(/[^0-9]/g, '');
  const waText = data?.whatsapp_message_template || 'I would like to verify my phone for withdrawals.';
  const waHref = waDigits
    ? `https://wa.me/${waDigits}?text=${encodeURIComponent(waText)}`
    : '#';

  const submit = async () => {
    const trimmed = phone.trim();
    if (!trimmed || trimmed.length < 5) {
      toast.error('Enter the phone number you texted us from');
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/users/me/phone-verification/request', { phone: trimmed });
      toast.success('Verification request submitted — admin will review shortly');
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.response?.data?.message || 'Submit failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[120] bg-black/75 backdrop-blur-sm flex items-end md:items-center md:justify-center p-3"
      onClick={() => !busy && onClose?.()}
      data-testid="phone-verify-dialog"
    >
      <div
        className="w-full md:w-[440px] rounded-2xl overflow-hidden relative"
        style={{ background: '#131822', border: '1px solid rgba(37,211,102,0.35)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-10 text-gray-400 hover:text-white"
          disabled={busy}
          data-testid="phone-verify-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        <div
          className="px-5 pt-6 pb-4 text-center"
          style={{ background: 'linear-gradient(135deg, rgba(37,211,102,0.15) 0%, rgba(212,175,55,0.10) 100%)' }}
        >
          <div
            className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-3"
            style={{ background: 'rgba(37,211,102,0.2)' }}
          >
            <Phone className="w-6 h-6 text-[#25D366]" />
          </div>
          <h3 className="text-xl font-bold text-white mb-1">Verify Your Phone</h3>
          <p className="text-xs text-gray-300 leading-snug max-w-sm mx-auto">
            One-time step before your first withdrawal. Text us on WhatsApp so we can confirm your identity — after approval, all future withdrawals are unlocked.
          </p>
        </div>

        {loading ? (
          <div className="p-6 text-center text-gray-400">Loading…</div>
        ) : data?.verified ? (
          <div className="p-5 text-center" data-testid="phone-verify-already-verified">
            <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-2" />
            <p className="font-bold text-white">You're already verified!</p>
            <button
              onClick={onClose}
              className="mt-4 px-5 py-2 rounded-lg text-sm font-bold text-[#0B0E14]"
              style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F5D76E 100%)' }}
            >
              Got it
            </button>
          </div>
        ) : data?.pending ? (
          <div className="p-5 space-y-3" data-testid="phone-verify-pending">
            <div
              className="rounded-lg p-3 flex items-start gap-2"
              style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)' }}
            >
              <Clock className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-300">Awaiting admin review</p>
                <p className="text-xs text-gray-300 mt-1">
                  We received your request for <b>{data.pending.phone}</b>. Usually approved within a few minutes.
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-full py-2.5 rounded-lg border border-white/10 text-sm text-gray-200 hover:bg-white/5"
              data-testid="phone-verify-pending-close"
            >
              Close — I'll check back later
            </button>
          </div>
        ) : !data?.enabled || !waNumber ? (
          <div className="p-5 text-center">
            <AlertCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
            <p className="text-sm text-gray-300">
              Phone verification is not configured. Please contact support.
            </p>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {data?.last_rejected && (
              <div
                className="rounded-lg p-3 flex items-start gap-2"
                style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)' }}
                data-testid="phone-verify-rejected-banner"
              >
                <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-300">Previous request rejected</p>
                  {data.last_rejected.rejection_reason && (
                    <p className="text-xs text-gray-300 mt-1">Reason: {data.last_rejected.rejection_reason}</p>
                  )}
                </div>
              </div>
            )}

            <div>
              <a
                href={waHref}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-xl font-bold text-white transition hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
                data-testid="phone-verify-whatsapp-btn"
              >
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm">Open WhatsApp — {waNumber}</span>
              </a>
              <p className="text-[10px] text-gray-500 mt-1.5 text-center">
                We'll pre-fill a verification message for you to send.
              </p>
            </div>

            <div>
              <label className="block text-xs text-gray-400 mb-1">Your phone number (the one you texted from)</label>
              <input
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 123 4567"
                className="w-full px-3 py-2.5 bg-[#0f1320] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-[#25D366]"
                data-testid="phone-verify-phone-input"
                disabled={busy}
              />
            </div>

            <button
              onClick={submit}
              disabled={busy || phone.trim().length < 5}
              className="w-full py-3 rounded-lg text-sm font-bold text-[#0B0E14] transition disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ background: 'linear-gradient(135deg, #D4AF37 0%, #F5D76E 100%)' }}
              data-testid="phone-verify-submit-btn"
            >
              {busy ? 'Submitting…' : "I've texted — request verification"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
